// PortalIcons.jsx — the portal's glyphs.
//
// ── SOLID, NOT OUTLINED ──
// Hairline outline sets (Feather, Lucide, Heroicons-outline) are what every
// admin template ships, and at 20px on a busy screen they read as grey noise —
// the eye has to resolve two edges and the space between them before it knows
// what the shape is. These are single filled silhouettes: one shape, one edge,
// legible at a glance and at any size, and they sit inside a chip rather than
// floating on the background.
//
// Grid: 24x24. Everything is `fill="currentColor"` — no strokes at all, so a
// glyph never thins out when it is scaled down and never needs a second set
// for the dark rail.
//
// ── WHY SEVERAL ARE NOT THE OBVIOUS SHAPE ──
// A few are drawn for this shop rather than for software in general: a mill
// wheel for the milling slots, sacks for bulk stock, a till roll for billing.
// A generic "aperture" standing in for a flour mill is how a menu ends up
// needing its labels read every time.

import React from "react";

function Glyph({ children, size = 21 }) {
  return (
    <svg
      className="mly-glyph"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/* ── the set ─────────────────────────────────────────────────────────────── */

const GLYPHS = {
  /* Dashboard — a house, door cut out of it. */
  home: (
    <path d="M12.7 2.4a1.1 1.1 0 0 0-1.4 0L2.9 9.1A1.8 1.8 0 0 0 2.2 10.5v8.7A2.4 2.4 0 0 0 4.6 21.6h3.9v-6.3a1.2 1.2 0 0 1 1.2-1.2h4.6a1.2 1.2 0 0 1 1.2 1.2v6.3h3.9a2.4 2.4 0 0 0 2.4-2.4v-8.7a1.8 1.8 0 0 0-.7-1.4Z" />
  ),

  /* Category — a crate with a band across the lid. */
  package: (
    <>
      <path d="M2.3 4.9a1.4 1.4 0 0 1 1.4-1.4h16.6a1.4 1.4 0 0 1 1.4 1.4v2.6a1.4 1.4 0 0 1-1.4 1.4H3.7a1.4 1.4 0 0 1-1.4-1.4Z" />
      <path d="M3.6 10.3h16.8v8.6a2.4 2.4 0 0 1-2.4 2.4H6a2.4 2.4 0 0 1-2.4-2.4Zm5.6 3.3a.9.9 0 0 0 0 1.8h5.6a.9.9 0 0 0 0-1.8Z" />
    </>
  ),

  /* Sub category — three stacked sheets. */
  layers: (
    <>
      <path d="M11.5 2.2a1.2 1.2 0 0 1 1 0l8.3 4.2a.8.8 0 0 1 0 1.4L12.5 12a1.2 1.2 0 0 1-1 0L3.2 7.8a.8.8 0 0 1 0-1.4Z" />
      <path d="M2.9 11.3a.8.8 0 0 1 1.1-.4l8 4 8-4a.8.8 0 1 1 .7 1.5l-8.3 4.2a1.2 1.2 0 0 1-1 0l-8.3-4.2a.8.8 0 0 1-.2-1.1Z" />
      <path d="M2.9 15.9a.8.8 0 0 1 1.1-.4l8 4 8-4a.8.8 0 1 1 .7 1.5l-8.3 4.2a1.2 1.2 0 0 1-1 0L3.1 17a.8.8 0 0 1-.2-1.1Z" />
    </>
  ),

  /* Product — a shop bag with the handle cut out. */
  "shopping-bag": (
    <path d="M8.9 7.1V6.6a3.1 3.1 0 0 1 6.2 0v.5h3.3a1.3 1.3 0 0 1 1.3 1.4l-.9 11a2.4 2.4 0 0 1-2.4 2.2H7.6a2.4 2.4 0 0 1-2.4-2.2l-.9-11a1.3 1.3 0 0 1 1.3-1.4Zm1.9 0h2.4v-.5a1.2 1.2 0 0 0-2.4 0Z" />
  ),

  /* Orders — a sealed carton with the seam showing. */
  box: (
    <>
      <path d="M11.1 2.3a1.8 1.8 0 0 1 1.8 0l7.3 4.2-3.5 2L9 4.4Zm-1.9 3.1 7.7 4.1-3.9 2.2a1 1 0 0 1-1 0L4.6 7.2Z" />
      <path d="M3.3 8.7 11 13v8.5l-7.2-4.1a1.4 1.4 0 0 1-.7-1.2Zm17.4 0v7.5a1.4 1.4 0 0 1-.7 1.2L12.8 21.5V13Z" />
    </>
  ),

  /* Bulk stock — two tied sacks. */
  sacks: (
    <>
      <path d="M15.3 2.6a.7.7 0 0 1 .6 1c-.3.6-.3 1.2 0 1.8 2.3.6 3.5 3 3.5 6 0 3.3-1.4 5.3-3.9 5.3s-3.9-2-3.9-5.3c0-3 1.2-5.4 3.5-6-.3-.6-.3-1.2 0-1.8a.7.7 0 0 1 .6-1Z" />
      <path d="M6.2 8.2a.7.7 0 0 1 .6 1c-.3.6-.3 1.2 0 1.8 2.1.6 3.2 2.8 3.2 5.5 0 3.1-1.3 4.9-3.6 4.9s-3.6-1.8-3.6-4.9c0-2.7 1.1-4.9 3.2-5.5-.3-.6-.3-1.2 0-1.8a.7.7 0 0 1 .6-1Z" />
    </>
  ),

  /* Price / tax — a tag with a percent punched through it. */
  percent: (
    <path d="M12.6 2.4H20a1.6 1.6 0 0 1 1.6 1.6v7.4a2.2 2.2 0 0 1-.6 1.5l-7.9 7.9a1.8 1.8 0 0 1-2.5 0l-6.4-6.4a1.8 1.8 0 0 1 0-2.5l7.9-7.9a2.2 2.2 0 0 1 1.5-.6Zm-3 6.1a1.3 1.3 0 1 0 1.8 1.8 1.3 1.3 0 0 0-1.8-1.8Zm4.6 3.3a1.3 1.3 0 1 0 1.8 1.8 1.3 1.3 0 0 0-1.8-1.8Zm.5-3.6-6.1 6.1a.8.8 0 0 0 1.1 1.1l6.1-6.1a.8.8 0 0 0-1.1-1.1Z" />
  ),

  /* Billing — a till roll with a torn edge. */
  receipt: (
    <path d="M5.4 3.3a1.2 1.2 0 0 1 1.2-1.2h10.8a1.2 1.2 0 0 1 1.2 1.2v18a.8.8 0 0 1-1.2.7l-1.8-1.1-1.8 1.1a.8.8 0 0 1-.8 0l-1.8-1.1-1.8 1.1a.8.8 0 0 1-.8 0l-1.8-1.1-1.8 1.1a.8.8 0 0 1-1.2-.7Zm3.3 3.3a.9.9 0 0 0 0 1.8h6.6a.9.9 0 0 0 0-1.8Zm0 3.8a.9.9 0 0 0 0 1.8h6.6a.9.9 0 0 0 0-1.8Zm0 3.8a.9.9 0 0 0 0 1.8h3.6a.9.9 0 0 0 0-1.8Z" />
  ),

  /* Staff — two heads, one behind. */
  users: (
    <>
      <path d="M9.4 11.6a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-6.6 8.1a5 5 0 0 1 5-5h3.2a5 5 0 0 1 5 5v.6a1.2 1.2 0 0 1-1.2 1.2H4a1.2 1.2 0 0 1-1.2-1.2Z" />
      <path d="M16.9 4.3a3.6 3.6 0 0 1 0 6.8 5.3 5.3 0 0 0 0-6.8Zm1.4 10.5a5 5 0 0 1 3 4.6v.7a1.2 1.2 0 0 1-.4.9h.9a.8.8 0 0 0 .8-.8v-.8a4.8 4.8 0 0 0-4.3-4.6Z" />
    </>
  ),

  /* Customer — a head with a basket beside it. */
  customer: (
    <>
      <path d="M9.9 10.6a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6Zm-6.6 9.5a4.8 4.8 0 0 1 4.8-4.8h3V21a.8.8 0 0 1-.8.8H4.3a1 1 0 0 1-1-1Z" />
      <path d="M15.1 12.4V11a1.9 1.9 0 0 1 3.8 0v1.4h1.9a1.1 1.1 0 0 1 1.1 1.2l-.7 6.6a1.8 1.8 0 0 1-1.8 1.6h-4.8a1.8 1.8 0 0 1-1.8-1.6l-.7-6.6a1.1 1.1 0 0 1 1.1-1.2Zm1.5 0h.8V11a.4.4 0 0 0-.8 0Z" />
    </>
  ),

  /* Offers — a tag with its eyelet punched out. */
  tag: (
    <path d="M12.6 2.4H20a1.6 1.6 0 0 1 1.6 1.6v7.4a2.2 2.2 0 0 1-.6 1.5l-7.9 7.9a1.8 1.8 0 0 1-2.5 0l-6.4-6.4a1.8 1.8 0 0 1 0-2.5l7.9-7.9a2.2 2.2 0 0 1 1.5-.6Zm4 3.4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4Z" />
  ),

  /* Loyalty — a rosette with its tails. */
  award: (
    <>
      <path d="M12 2.4a5.9 5.9 0 1 0 0 11.8 5.9 5.9 0 0 0 0-11.8Z" />
      <path d="M7.6 15a7.6 7.6 0 0 0 2.4.9l-1.3 5.6a.7.7 0 0 1-1.2.3l-1.7-1.9-2.5.3a.7.7 0 0 1-.7-.9Zm8.8 0 5 4.3a.7.7 0 0 1-.7.9l-2.5-.3-1.7 1.9a.7.7 0 0 1-1.2-.3L14 15.9a7.6 7.6 0 0 0 2.4-.9Z" />
    </>
  ),

  /* Support — a ring buoy, hole through the middle. */
  "life-buoy": (
    <path d="M12 2.1a9.9 9.9 0 1 0 0 19.8 9.9 9.9 0 0 0 0-19.8Zm0 3.3a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2Zm0 2.7a3.9 3.9 0 1 0 0 7.8 3.9 3.9 0 0 0 0-7.8Z" />
  ),

  /* Banner — a framed picture with sun and hill. */
  image: (
    <path d="M4.6 3.6h14.8a2.4 2.4 0 0 1 2.4 2.4v12a2.4 2.4 0 0 1-2.4 2.4H4.6a2.4 2.4 0 0 1-2.4-2.4V6a2.4 2.4 0 0 1 2.4-2.4Zm3.4 3.1a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6Zm-4 10.9h15.9v-1.4l-3.7-3.6a1.4 1.4 0 0 0-2 0l-1.9 1.8-3.3-3.2a1.4 1.4 0 0 0-2 0L4 15.6Z" />
  ),

  /* Branding — a palette with its thumb hole and three wells. */
  palette: (
    <path d="M12 2.3C6.5 2.3 2.2 6.6 2.2 12s4.3 9.7 9.8 9.7a2.2 2.2 0 0 0 1.8-3.5 2.2 2.2 0 0 1 1.8-3.5h2.2a4.1 4.1 0 0 0 4-4.1c0-4.4-4.3-8.3-9.8-8.3ZM7.3 10.3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm3-3.9a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm5.4 1a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" />
  ),

  /* Notification — a bell with the clapper below it. */
  bell: (
    <>
      <path d="M12 2.2a6.4 6.4 0 0 0-6.4 6.4c0 4.3-1.8 5.9-2.2 6.3a.9.9 0 0 0 .6 1.6h16a.9.9 0 0 0 .6-1.6c-.4-.4-2.2-2-2.2-6.3A6.4 6.4 0 0 0 12 2.2Z" />
      <path d="M9.6 18.1h4.8a2.4 2.4 0 0 1-4.8 0Z" />
    </>
  ),

  /* Stores — a map pin, hole through it. */
  "map-pin": (
    <path d="M12 2.2a7.6 7.6 0 0 0-7.6 7.6c0 5.6 6.8 11.6 7.1 11.9a.8.8 0 0 0 1 0c.3-.3 7.1-6.3 7.1-11.9A7.6 7.6 0 0 0 12 2.2Zm0 4.9a2.8 2.8 0 1 1 0 5.6 2.8 2.8 0 0 1 0-5.6Z" />
  ),

  /* Store settings — two faders with solid knobs. */
  sliders: (
    <>
      <path d="M2.4 6.6h4a3.1 3.1 0 0 1 5.9 0h9.3a1 1 0 0 1 0 2h-9.3a3.1 3.1 0 0 1-5.9 0h-4a1 1 0 0 1 0-2Z" />
      <path d="M2.4 15.4h9.3a3.1 3.1 0 0 1 5.9 0h4a1 1 0 0 1 0 2h-4a3.1 3.1 0 0 1-5.9 0H2.4a1 1 0 0 1 0-2Z" />
    </>
  ),

  /* Live view — a camera body with the lens barrel. */
  video: (
    <path d="M3.4 6h10.2a2.4 2.4 0 0 1 2.4 2.4v7.2a2.4 2.4 0 0 1-2.4 2.4H3.4A2.4 2.4 0 0 1 1 15.6V8.4A2.4 2.4 0 0 1 3.4 6Zm14.3 4.6 3.1-1.8a.9.9 0 0 1 1.4.8v4.8a.9.9 0 0 1-1.4.8l-3.1-1.8Z" />
  ),

  /* Milling slots — a mill wheel: hub, rim and eight spokes. */
  mill: (
    <path d="M12 2.1a9.9 9.9 0 1 0 0 19.8 9.9 9.9 0 0 0 0-19.8Zm0 2.6a1 1 0 0 1 1 1v3.1a3.4 3.4 0 0 0-2 0V5.7a1 1 0 0 1 1-1Zm-5.1 2.1a1 1 0 0 1 1.4 0l2.2 2.2a3.4 3.4 0 0 0-1.4 1.4L6.9 8.2a1 1 0 0 1 0-1.4Zm10.2 0a1 1 0 0 1 0 1.4l-2.2 2.2a3.4 3.4 0 0 0-1.4-1.4l2.2-2.2a1 1 0 0 1 1.4 0ZM12 9.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4ZM5.7 11h3.1a3.4 3.4 0 0 0 0 2H5.7a1 1 0 0 1 0-2Zm9.5 0h3.1a1 1 0 0 1 0 2h-3.1a3.4 3.4 0 0 0 0-2Zm-5.7 3.6a3.4 3.4 0 0 0 1.4 1.4l-2.2 2.2a1 1 0 1 1-1.4-1.4Zm4.6 0 2.2 2.2a1 1 0 1 1-1.4 1.4l-2.2-2.2a3.4 3.4 0 0 0 1.4-1.4ZM12 15.6a3.4 3.4 0 0 0 1 .2v3.1a1 1 0 0 1-2 0v-3.1a3.4 3.4 0 0 0 1-.2Z" />
  ),

  /* Reports — a sheet with the corner folded and lines on it. */
  "file-text": (
    <>
      <path d="M13.4 2.3H6.9a2.4 2.4 0 0 0-2.4 2.4v14.6a2.4 2.4 0 0 0 2.4 2.4h10.2a2.4 2.4 0 0 0 2.4-2.4V8.2h-4.7a1.4 1.4 0 0 1-1.4-1.4Zm-4.6 9.4h6.4a.9.9 0 0 1 0 1.8H8.8a.9.9 0 0 1 0-1.8Zm0 3.7h4.2a.9.9 0 0 1 0 1.8H8.8a.9.9 0 0 1 0-1.8Z" />
      <path d="M15.2 2.6 19.4 6.6h-4.2Z" />
    </>
  ),

  /* Audit — a shield with the tick cut out of it. */
  shield: (
    <path d="M11.6 2.2a1.2 1.2 0 0 1 .8 0l7.1 3.1a1.2 1.2 0 0 1 .7 1.1v5.4c0 5.2-3.4 9-8.1 10.6a1.2 1.2 0 0 1-.8 0C6.6 20.8 3.2 17 3.2 11.8V6.4a1.2 1.2 0 0 1 .7-1.1Zm4.1 6.3a1 1 0 0 0-1.4.1l-3.5 3.9-1.6-1.6a1 1 0 1 0-1.4 1.4l2.4 2.4a1 1 0 0 0 1.4 0l4.2-4.7a1 1 0 0 0-.1-1.5Z" />
  ),

  /* Settings — a cog with the hub punched out. */
  settings: (
    <path d="M10.3 2.1h3.4a1 1 0 0 1 1 .8l.3 1.9a7.9 7.9 0 0 1 1.5.9l1.8-.7a1 1 0 0 1 1.2.4l1.7 2.9a1 1 0 0 1-.2 1.3l-1.5 1.2a8 8 0 0 1 0 1.7l1.5 1.2a1 1 0 0 1 .2 1.3l-1.7 2.9a1 1 0 0 1-1.2.4l-1.8-.7a7.9 7.9 0 0 1-1.5.9l-.3 1.9a1 1 0 0 1-1 .8h-3.4a1 1 0 0 1-1-.8l-.3-1.9a7.9 7.9 0 0 1-1.5-.9l-1.8.7a1 1 0 0 1-1.2-.4l-1.7-2.9a1 1 0 0 1 .2-1.3l1.5-1.2a8 8 0 0 1 0-1.7L3 9.6a1 1 0 0 1-.2-1.3l1.7-2.9a1 1 0 0 1 1.2-.4l1.8.7a7.9 7.9 0 0 1 1.5-.9l.3-1.9a1 1 0 0 1 1-.8ZM12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" />
  ),

  /* Bill designer — a sheet with a nib over it. */
  "bill-design": (
    <>
      <path d="M12.6 2.6H6.7a2.4 2.4 0 0 0-2.4 2.4v14a2.4 2.4 0 0 0 2.4 2.4h10a2.4 2.4 0 0 0 2.4-2.4v-5.1l-5.4 5.4a1.6 1.6 0 0 1-.8.4l-3.4.7a1 1 0 0 1-1.2-1.2l.7-3.4a1.6 1.6 0 0 1 .4-.8Zm-4 9.2h3a.9.9 0 0 1 0 1.8h-3a.9.9 0 0 1 0-1.8Z" />
      <path d="m17.5 3.2 3.3 3.3-7.9 7.9-3.9.6.6-3.9Zm4.2 2.4-3.3-3.3.6-.6a2 2 0 0 1 2.8 0l.5.5a2 2 0 0 1 0 2.8Z" />
    </>
  ),

  /* Permissions — a key, bit and all. */
  key: (
    <path d="M20.4 2.4a1 1 0 0 1 0 1.4l-1.2 1.2 1.5 1.5a1 1 0 0 1-1.4 1.4l-1.5-1.5-1.3 1.3 1.5 1.5a1 1 0 1 1-1.4 1.4l-1.5-1.5-3.3 3.3a5.8 5.8 0 1 1-1.4-1.4L19 2.4a1 1 0 0 1 1.4 0ZM7.5 14.2a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z" />
  ),
};

/* Names the menu used before this set existed, so an entry that has not been
   updated still renders something rather than a gap in the rail. */
const ALIASES = {
  "user-minus": "customer",
  aperture: "mill",
  briefcase: "sacks",
  map: "map-pin",
};

export default function PortalIcon({ name, size = 21 }) {
  const glyph = GLYPHS[name] || GLYPHS[ALIASES[name]];
  if (!glyph) return null;
  return <Glyph size={size}>{glyph}</Glyph>;
}

export const GLYPH_NAMES = Object.keys(GLYPHS);
